// Vibe shelf presets for the Home tile grid (FOS-HOME-SHELVES-EXPAND-001).
//
// This rule table is the SEED for the catalog vibe-tag lane
// (FOS-CATALOG-VIBE-TAGS-001) and the FALLBACK while a bottle carries no
// effective vibe tags. When collection_display exposes `vibe_tags`, a vibe
// shelf reads those first (ranked by confidence) and only falls back to these
// accord / note / wear-context rules for bottles that have no tags yet.
//
// Names use creator language on purpose (Rob, DC-HOME-SHELVES-EXPAND-20260920-01).
// Labels are editable by the user; ids and tag mappings are stable.

const preset = (id, label, rules) => Object.freeze({
  id, label,
  accords: Object.freeze(rules.accords || []),
  notes: Object.freeze(rules.notes || []),
  contexts: Object.freeze(rules.contexts || []),
  tags: Object.freeze(rules.tags || []),
  weights: Object.freeze({ accord: 4, note: 2, wear: 2, compliment: 1, ...(rules.weights || {}) }),
  performance: Boolean(rules.performance),
  lowProjection: Boolean(rules.lowProjection),
  complimentsOnly: Boolean(rules.complimentsOnly),
})

export const VIBE_PRESETS = Object.freeze([
  preset('sexy', 'Sexy', {
    accords: ['amber', 'vanilla', 'musky', 'musk', 'sweet', 'warm spicy', 'spicy', 'tobacco', 'leather', 'oud'],
    notes: ['vanilla', 'amber', 'musk', 'tonka', 'tobacco', 'leather', 'benzoin', 'labdanum', 'oud', 'rum', 'cinnamon'],
    contexts: ['evening', 'date', 'date night', 'night out', 'dinner', 'romantic'],
    tags: ['sexy', 'date_night'],
    weights: { compliment: 1 },
  }),
  preset('clean_fresh', 'Clean & Fresh', {
    accords: ['citrus', 'aquatic', 'marine', 'aromatic', 'green', 'fresh spicy', 'fresh', 'white musk', 'soapy', 'ozonic'],
    notes: ['bergamot', 'lemon', 'lime', 'grapefruit', 'mandarin', 'sea notes', 'sea salt', 'mint', 'lavender', 'white musk', 'cotton', 'vetiver', 'green notes', 'ambroxan'],
    contexts: ['day', 'daytime', 'daily', 'casual', 'errands', 'office', 'work'],
    tags: ['clean', 'fresh'],
  }),
  preset('cozy', 'Cozy', {
    accords: ['gourmand', 'vanilla', 'sweet', 'balsamic', 'powdery', 'coconut', 'warm spicy', 'caramel'],
    notes: ['vanilla', 'tonka', 'caramel', 'praline', 'coconut', 'cocoa', 'chocolate', 'honey', 'almond', 'benzoin', 'sandalwood', 'iris'],
    contexts: ['evening', 'home', 'cozy', 'winter'],
    tags: ['cozy', 'gourmand'],
  }),
  preset('smell_like_a_boss', 'Smell Like a Boss', {
    accords: ['amber', 'spicy', 'warm spicy', 'leather', 'woody', 'tobacco', 'oud'],
    notes: ['amber', 'pepper', 'cardamom', 'leather', 'cedar', 'tobacco', 'oud', 'saffron'],
    contexts: ['evening', 'office', 'work'],
    tags: ['smell_like_a_boss'],
    weights: { wear: 3, compliment: 3 },
  }),
  preset('beast_mode', 'Beast Mode', {
    accords: ['amber', 'spicy', 'warm spicy', 'smoky', 'tobacco', 'leather', 'oud', 'animalic'],
    notes: ['oud', 'tobacco', 'leather', 'incense', 'amber', 'patchouli', 'birch', 'cade', 'saffron'],
    contexts: ['evening', 'night out', 'club', 'clubbing', 'winter'],
    tags: ['beast_mode'],
    performance: true,
  }),
  preset('compliment_getters', 'Compliment Getters', {
    // Same source as the Most Complimented tile: recorded compliments only.
    tags: [],
    complimentsOnly: true,
  }),
  preset('office_safe', 'Office Safe', {
    accords: ['citrus', 'aromatic', 'green', 'fresh spicy', 'powdery', 'woody', 'soft'],
    notes: ['bergamot', 'iris', 'vetiver', 'cedar', 'lavender', 'tea', 'white musk', 'green notes'],
    contexts: ['office', 'work', 'day', 'daytime', 'daily'],
    tags: ['office_safe'],
    lowProjection: true,
  }),
  preset('naughty', 'Naughty', {
    accords: ['amber', 'vanilla', 'musky', 'musk', 'sweet', 'warm spicy', 'animalic', 'honey', 'powdery'],
    notes: ['vanilla', 'amber', 'musk', 'tonka', 'honey', 'jasmine', 'tuberose', 'ylang ylang', 'benzoin', 'cumin', 'civet'],
    contexts: ['intimate', 'private', 'bedroom', 'evening'],
    tags: ['naughty', 'intimate'],
  }),
  preset('beach', 'Beach', {
    accords: ['coconut', 'aquatic', 'marine', 'ozonic', 'solar', 'amber', 'vanilla', 'white floral'],
    notes: ['coconut', 'sea salt', 'ambergris', 'tiare', 'solar notes', 'sand accord', 'driftwood', 'vanilla', 'bergamot', 'ylang ylang'],
    contexts: ['beach', 'summer', 'vacation', 'tropical', 'coastal'],
    tags: ['beach', 'tropical', 'coastal'],
  }),
  preset('boozy', 'Boozy', {
    accords: ['rum', 'sweet', 'warm spicy', 'spicy', 'vanilla', 'tobacco', 'amber', 'woody'],
    notes: ['rum', 'cognac', 'whiskey', 'brandy', 'davana', 'cinnamon', 'vanilla', 'tonka', 'tobacco', 'oak', 'cherry'],
    contexts: ['evening', 'night', 'bar', 'lounge', 'winter'],
    tags: ['boozy', 'alcoholic'],
  }),
])

export const vibePreset = id => VIBE_PRESETS.find(item => item.id === id) || null
export const vibePresetLabel = id => vibePreset(id)?.label || 'Vibe'

const STRONG_CHECKINS = new Set(['strong', 'projecting above average'])

// Performance evidence from the collector's own wear logs. Returns null when
// no log carries a check-in or a longevity figure for this bottle.
export function performanceSignal(logs = []) {
  let strong = 0
  let checkins = 0
  let hours = []
  for (const log of logs) {
    for (const checkin of Array.isArray(log?.performance_checkins) ? log.performance_checkins : []) {
      checkins += 1
      if (STRONG_CHECKINS.has(String(checkin?.value || '').toLocaleLowerCase())) strong += 1
    }
    const logged = Number(log?.longevity_hours || log?.duration_hours || 0)
    if (logged > 0) hours.push(logged)
  }
  if (!checkins && !hours.length) return null
  const averageHours = hours.length ? hours.reduce((sum, value) => sum + value, 0) / hours.length : 0
  return { strong, checkins, averageHours, beast: strong > 0 || averageHours >= 8 }
}
